<template>
    <div class="hotspot-panel" v-bind:class="{ 'active' : isHotspotPanelVisible }">

        <button class="back-to-360" tabindex="0" v-el:close-bt v-on:click="closePanel" aria-label="Close and back to the threesixty view">
            <div class="cross"></div>
        </button>

        <div class="row row-full">
            <div class="no-gutter hotspot-imagery">

                <div class="hotspot-xray-area" v-el:xray-area v-bind:style="{ backgroundImage: 'url(' + hotspot.image + ')' }">
                    <div class="mobile-overlay"></div>
                    <svg class="xray">
                        <defs>
                            <clipPath id="clipping-{{ hotspot.id }}">
                                <circle
                                    class="xray-ellipse"
                                    id="ellipse-{{ hotspot.id }}"
                                    v-el:xray-ellipse
                                    v-bind:cx="this.xrayCX + '%'"
                                    v-bind:cy="this.xrayCY + '%'"
                                    v-bind:r="this.xrayRadius + '%'">
                                    <animate class="animate-reset-cx" v-el:xray-cx-reset-anim attributeName="cx" begin="indefinite" dur=".5s" fill="freeze" to="50%"/>
                                    <animate class="animate-reset-cy" v-el:xray-cy-reset-anim attributeName="cy" begin="indefinite" dur=".5s" fill="freeze" to="50%"/>
                                </circle>
                            </clipPath>
                        </defs>
                    </svg>
                    <svg>
                        <use xlink:href="#ellipse-{{ hotspot.id }}" />
                        <image
                            xlink:href="{{ hotspot.imageXray }}"
                            x="0"
                            y="0"
                            width="100%"
                            height="100%"
                            preserveAspectRatio="xMidYMid slice"
                            v-bind:style="{ clipPath: 'url(#clipping-' + hotspot.id + ')' }"
                            v-on:click="expandCollapseContent" />
                    </svg>
                </div>

                <button class="previous-hotspot" v-on:click.prevent="previousSpot()"><i class="chevron-left-white@22"></i></button>
                <button class="next-hotspot" v-on:click.prevent="nextSpot()"><i class="chevron-right-white@22"></i></button>

                <div class="hotspot-mobile-line">
                    <ul class="hotspot-mobile-line-dots">
                        <li>
                            <span class="hotspot-number">{{ this.previousHotspotIndex() + 1 | addZero }}</span>
                        </li>
                        <li>
                            <span class="hotspot-number">{{ hotspotNumber + 1 | addZero }}</span>
                            <span class="hotspot-title">{{ hotspot.name }}</span>
                        </li>
                        <li>
                            <span class="hotspot-number">{{ this.nextHotspotIndex() + 1 | addZero }}</span>
                        </li>
                    </ul>
                </div>

            </div>
            <div class="gr-6@large gr-5@xlarge gr-4@xxlarge no-gutter hotspot-content" v-bind:class="{ 'expanded' : isGlobalOverlayEnabled }" v-if="hotspot">

                <button class="mobile-button expand-collapse-bt" v-on:click="expandCollapseContent">
                    Learn about his {{ hotspot.name }} <span class="icon"><i class="arrow-up-black@22 size@16"></i></span>
                </button>

                <span class="label">
                    {{ hotspotNumber + 1 | addZero }} / {{ totalHotspots | addZero }}
                </span>

                <h1 class="_h2 title" role="heading">{{ hotspot.title }}</h1>

                <div class="scrolly-me-container" v-el:scrolly-me>
                    <div class="scrolly-me">

                        <div class="markdown" v-if="hotspot.content">
                            {{{ hotspot.content | markdown }}}
                        </div>

                    </div>
                </div>

            </div>
        </div>

    </div>
</template>

<script>
import Sound from '../components/Sound'
import perfectScrollbar from 'perfect-scrollbar'
import _throttle from 'lodash.throttle'
import Hammer from 'hammerjs'
import { enableGlobalOverlay, disableGlobalOverlay, openHotspotPanel, closeHotspotPanel } from '../vuex/actions'
import HotspotsNav from '../components/HotspotsNav'

export default {
    vuex: {
        getters: {
            isGlobalOverlayEnabled: state => state.isGlobalOverlayEnabled,
            isHotspotPanelOpened: state => state.isHotspotPanelOpened,
            hotspot: state => state.hotspot,
            totalHotspots: state => state.totalHotspots,
            hotspotNumber: state => state.hotspotNumber,
            hotspotsContent: state => state.hotspotsContent,
            isMobileSize: state => state.isMobileSize,
            isMobileAgent: state => state.isMobileAgent,
            isBrowserSafari: state => state.isBrowserSafari
        },
        actions: {
            enableGlobalOverlay,
            disableGlobalOverlay,
            closeHotspotPanel,
            openHotspotPanel
        }
    },

    data: function () {
        return {
            isFirstLoad: true,
            isHotspotPanelVisible: false,
            scrollbar: false,
            resizeHandler: false,
            mouseHandler: false,

            xrayAreaWidth: false,
            xrayAreaHeight: false,
            xrayAreaOffsetLeft: false,
            xrayAreaOffsetTop: false,
            xrayRadius: 0,
            xrayRadiusPx: 0,
            xrayCX: 50,
            xrayCY: 50,
            xrayAnimStart: false,
            xrayRequestAnim: false,
            xrayEllipseIsOutside: false,
            xrayDeltaX: 0,
            xrayDeltaY: 0,

            enableXrayInteraction: true
        }
    },

    methods: {

        'xrayResetAnim': function (timestamp) {
            if (!this.xrayAnimStart) this.xrayAnimStart = timestamp
            let progress = timestamp - this.xrayAnimStart

            // Wait 2 seconds before executing animation
            if (progress < 2000) {
                if (this.xrayRequestAnim !== false) {
                    this.xrayRequestAnim = window.requestAnimationFrame(this.xrayResetAnim)
                }
            } else {
                if (this.$els.xrayCxResetAnim !== null && 'beginElement' in this.$els.xrayCxResetAnim && !this.isBrowserSafari) {
                    this.$els.xrayCxResetAnim.beginElement()
                    this.$els.xrayCyResetAnim.beginElement()
                } else {
                    this.xrayMove()
                }
                this.xrayRequestAnim = false
            }
        },

        'xrayCancelResetAnim': function () {
            if (this.xrayRequestAnim !== false) {
                window.cancelAnimationFrame(this.xrayRequestAnim)
            }
            this.$set('xrayRequestAnim', false)
            this.$set('xrayAnimStart', false)
        },

        'xrayBeginResetAnim': function () {
            this.xrayCancelResetAnim()
            this.xrayRequestAnim = window.requestAnimationFrame(this.xrayResetAnim)
        },

        'getCX': function (mouseX) {
            return ((mouseX - this.xrayAreaOffsetLeft) / this.xrayAreaWidth * 100).toFixed(2)
        },

        'getCY': function (mouseY) {
            return ((mouseY - this.xrayAreaOffsetTop) / this.xrayAreaHeight * 100).toFixed(2)
        },

        'xrayMove': function (mouseX = false, mouseY = false) {
            if (this.$els.xrayArea !== null) {
                if (!this.isMobileSize && mouseX !== false && mouseY !== false) {
                    // is ellipse outside its container minus its limit?
                    let hitLeftLimit = mouseX - this.xrayRadiusPx < this.xrayAreaOffsetLeft
                    let hitRightLimit = mouseX + this.xrayRadiusPx > this.xrayAreaOffsetLeft + this.xrayAreaWidth
                    let hitTopLimit = mouseY - this.xrayRadiusPx < this.xrayAreaOffsetTop
                    let hitBottomLimit = mouseY + this.xrayRadiusPx > this.xrayAreaOffsetTop + this.xrayAreaHeight

                    let cx = this.getCX(mouseX)
                    let cy = this.getCY(mouseY)
                    this.$set('xrayCX', cx)
                    this.$set('xrayCY', cy)

                    if (hitLeftLimit) {
                        this.$set('xrayCX', this.getCX(this.xrayRadiusPx))
                    }
                    if (hitRightLimit) {
                        this.$set('xrayCX', this.getCX(this.xrayAreaWidth - this.xrayRadiusPx))
                    }
                    if (hitTopLimit) {
                        this.$set('xrayCY', this.getCY(this.xrayRadiusPx))
                    }
                    if (hitBottomLimit) {
                        this.$set('xrayCY', this.getCY(this.xrayAreaHeight - this.xrayRadiusPx))
                    }

                    let isOutside = (hitLeftLimit || hitRightLimit || hitTopLimit || hitBottomLimit)
                    if (isOutside) {
                        this.xrayBeginResetAnim()
                    } else {
                        this.xrayCancelResetAnim()
                    }

                } else {
                    this.xrayCancelResetAnim()
                    this.$set('xrayCX', 50)
                    this.$set('xrayCY', 50)
                }

            }
        },

        'bindEvents': function () {
            // window resizing
            this.resizeHandler = _throttle(function () {
                this.checkMobile()
                if (this.scollbar) this.scrollbar.update(this.$els.scrollyMe)
            }.bind(this), 500)
            window.addEventListener('resize', this.resizeHandler, false)

            // xray interactions
            this.mouseHandler = _throttle(function (event) {
                this.xrayMove(event.clientX, event.clientY)
            }.bind(this), 5)
            if (this.enableXrayInteraction && this.$els.xrayArea !== null) {
                this.xrayMove()
                this.$els.xrayArea.addEventListener('mousemove', this.mouseHandler, false)
            }

            // trigger resize
            setTimeout(function () {
                let event = document.createEvent('CustomEvent')
                event.initCustomEvent('resize', true, true, {})
                window.dispatchEvent(event)
            }, 650)
        },

        'unbindEvents': function () {
            // resizing
            window.removeEventListener('resize', this.resizeHandler, false)

            // Interactions
            if (this.enableXrayInteraction) {
                window.removeEventListener('mousemove', this.mouseHandler, false)
            }
        },

        'checkMobile': function () {
            if (this.$els.xrayArea !== null) {
                this.$set('xrayAreaWidth', this.$els.xrayArea.offsetWidth)
                this.$set('xrayAreaHeight', this.$els.xrayArea.offsetHeight)
                this.$set('xrayAreaOffsetLeft', this.$els.xrayArea.getBoundingClientRect().left)
                this.$set('xrayAreaOffsetTop', this.$els.xrayArea.getBoundingClientRect().top)

                if (this.isMobileSize) {
                    this.$set('xrayRadius', 18)
                } else {
                    this.$set('xrayRadius', 15)
                    if (this.isGlobalOverlayEnabled) {
                        this.disableGlobalOverlay()
                    }
                }

                // Radius in pixels (+5 is border size)
                this.$nextTick(function () {
                    this.$set('xrayRadiusPx', ((this.$els.xrayEllipse.getBoundingClientRect().width / 2) + 5))

                    if (this.isFirstLoad) {
                        if (!this.isMobileSize && this.isMobileAgent) {
                            this.enableTouch()
                        } else if (this.isMobileSize) {
                            this.enableSwipe()
                        }
                        this.$set('isFirstLoad', false)
                    }
                })
            }
        },

        'previousHotspotIndex': function () {
            return (this.hotspotNumber > 0) ? (this.hotspotNumber - 1) : this.totalHotspots - 1
        },

        'nextHotspotIndex': function () {
            return (this.hotspotNumber < this.totalHotspots - 1) ? (this.hotspotNumber + 1) : 0
        },

        'nextSpot': function () {
            this.unbindEvents()
            this.resetScrollPos()
            let nextIndex = this.nextHotspotIndex()
            let nextHotspot = this.hotspotsContent[nextIndex]
            this.openHotspotPanel(nextHotspot, this.hotspotsContent)
            this.bindEvents()

            this.$router.go({
                name: 'graham',
                params: {
                    hotspot: nextHotspot.id
                }
            })
        },

        'previousSpot': function () {
            this.unbindEvents()
            this.resetScrollPos()
            let previousIndex = this.previousHotspotIndex()
            let previousHotspot = this.hotspotsContent[previousIndex]
            this.openHotspotPanel(previousHotspot, this.hotspotsContent)
            this.bindEvents()

            this.$router.go({
                name: 'graham',
                params: {
                    hotspot: previousHotspot.id
                }
            })
        },

        'resetScrollPos': function () {
            this.$els.scrollyMe.scrollTop = 0
        },

        'enableSwipe': function () {
            let hammertime = new Hammer(this.$els.xrayArea, {
                preventDefault: true
            })

            let throttleValue = 125

            hammertime.get('swipe').set({
                direction: Hammer.DIRECTION_HORIZONTAL
            })

            hammertime.on('swipeleft', _throttle(function (ev) {
                if (this.isMobileSize) this.nextSpot()
            }.bind(this), throttleValue))

            hammertime.on('swiperight', _throttle(function (ev) {
                if (this.isMobileSize) this.previousSpot()
            }.bind(this), throttleValue))
        },

        'closePanel': function (redirect = true) {
            this.$set('isHotspotPanelVisible', false)
            this.unbindEvents()
            this.xrayCancelResetAnim()

            // Closing anim
            setTimeout(function () {
                this.closeHotspotPanel()
                if (redirect) {
                    this.$router.go({
                        name: 'view'
                    })
                }
            }.bind(this), 600)
        },

        'expandCollapseContent': function () {
            if (this.isMobileSize) {
                if (this.isGlobalOverlayEnabled) {
                    this.disableGlobalOverlay()
                } else {
                    this.enableGlobalOverlay()
                }
            }
        },

        'enableTouch': function () {
            let hammertime = new Hammer(this.$els.xrayArea, {
                preventDefault: true
            })

            let throttleValue = 10

            hammertime.get('pan').set({
                direction: Hammer.DIRECTION_ALL,
                threshold: 10
            })

            hammertime.on('panmove', _throttle(function (event) {
                let dX = this.xrayDeltaX + (event.deltaX)
                let dY = this.xrayDeltaY + (event.deltaY)
                let offsetTouchX = (this.xrayAreaWidth / 2) + dX
                let offsetTouchY = (this.xrayAreaHeight / 2) + dY
                let outsideLeft = ((offsetTouchX - this.xrayRadiusPx + this.xrayAreaOffsetLeft) < this.xrayAreaOffsetLeft)
                let outsideRight = ((offsetTouchX + this.xrayRadiusPx + this.xrayAreaOffsetLeft) > (this.xrayAreaOffsetLeft + this.xrayAreaWidth))
                let outsideTop = ((offsetTouchY - this.xrayRadiusPx + this.xrayAreaOffsetTop) < this.xrayAreaOffsetTop)
                let outsideBottom = ((offsetTouchY + this.xrayRadiusPx + this.xrayAreaOffsetTop) > (this.xrayAreaOffsetTop + this.xrayAreaHeight))

                // Contain ellipse within container
                if (outsideLeft && outsideTop) {
                    // Top Left Corner
                    this.$set('xrayCX', this.getCX(this.xrayRadiusPx))
                    this.$set('xrayCY', this.getCY(this.xrayRadiusPx))
                } else if (outsideRight && outsideTop) {
                    // Top Right Corner
                    this.$set('xrayCX', this.getCX(this.xrayAreaWidth - this.xrayRadiusPx))
                    this.$set('xrayCY', this.getCY(this.xrayRadiusPx))
                } else if (outsideLeft && outsideBottom) {
                    // Bottom Left Corner
                    this.$set('xrayCX', this.getCX(this.xrayRadiusPx))
                    this.$set('xrayCY', this.getCY(this.xrayAreaHeight - this.xrayRadiusPx))
                } else if (outsideRight && outsideBottom) {
                    // Bottom Right Corner
                    this.$set('xrayCX', this.getCX(this.xrayAreaWidth - this.xrayRadiusPx))
                    this.$set('xrayCY', this.getCY(this.xrayAreaHeight - this.xrayRadiusPx))
                } else if (outsideLeft) {
                    // Left
                    this.$set('xrayCX', this.getCX(this.xrayRadiusPx))
                    this.$set('xrayCY', this.getCY(offsetTouchY))
                } else if (outsideRight) {
                    // Right
                    this.$set('xrayCX', this.getCX(this.xrayAreaWidth - this.xrayRadiusPx))
                    this.$set('xrayCY', this.getCY(offsetTouchY))
                } else if (outsideTop) {
                    // Top
                    this.$set('xrayCX', this.getCX(offsetTouchX))
                    this.$set('xrayCY', this.getCY(this.xrayRadiusPx))
                } else if (outsideBottom) {
                    // Bottom
                    this.$set('xrayCX', this.getCX(offsetTouchX))
                    this.$set('xrayCY', this.getCY(this.xrayAreaHeight - this.xrayRadiusPx))
                } else {
                    // If inside the container
                    this.$set('xrayCX', this.getCX(offsetTouchX))
                    this.$set('xrayCY', this.getCY(offsetTouchY))
                }

            }.bind(this), throttleValue))

            hammertime.on('panend', _throttle(function (event) {
                this.xrayDeltaX = this.xrayDeltaX + event.deltaX
                this.xrayDeltaY = this.xrayDeltaY + event.deltaY
            }.bind(this), throttleValue))
        }
    },

    components: {
        'sound-component': Sound,
        'hotspots-nav-component': HotspotsNav
    },

    ready: function () {
        this.$nextTick(function () {
            // Scrollbar Y axis init
            this.scrollbar = perfectScrollbar.initialize(this.$els.scrollyMe, {
                suppressScrollX: true
            })

            // Check if mobile
            setTimeout(function () {
                this.checkMobile()
                this.bindEvents()
                if (this.$els.closeBt != null) this.$els.closeBt.focus()
            }.bind(this), 1500)

            // Reveal animation
            this.$set('isHotspotPanelVisible', true)
        })
    }
}
</script>

<style lang="scss">
    @import '../assets/sass/global';

    $spot-closed: 160px;
    $spot-opened: 800px;
    $close-bt-size: 46.06px;
    $close-bt-size-large: 51.19px;

    .-global-overlay .hotspot-panel .back-to-360 {
        transform: translateY(-10rem);
    }

    @keyframes hotspot-panel-in {
        0% {
            width: 0;
        }
        50% {
            width: 75%;
        }
        100% {
            width: 100%;
        }
    }

    @keyframes hotspot-panel-out {
        0% {
            width: 100%;
        }
        50% {
            width: 75%;
        }
        100% {
            width: 0;
        }
    }

    @keyframes up-n-down {
        0% {
            transform: translateY(-3px);
        }
        50% {
            transform: translateY(6px);
        }
        100% {
            transform: translateY(-3px);
        }
    }

    .hotspot-panel {
        display: block;
        position: fixed;
        height: 100%;
        top: 0;
        right: 0;
        z-index: 195;
        overflow: hidden;
        width: 0;
        opacity: 0;

        @include gridle_state(large) {
            transition: opacity .2s ease .6s;
            animation: hotspot-panel-out .4s ease forwards;
        }

        .back-to-360 {
            transition: transform 0.4s ease-out 0.3s;
            transform: translateY(0);
            background-color: $towards-blue;
            position: absolute;
            display: block;
            width: $close-bt-size;
            height: $close-bt-size;
            border-radius: 50%;
            right: $global-viewport-padding-mobile;
            top: $global-viewport-padding-mobile;
            cursor: pointer;
            z-index: 99;
            opacity: 0;

            @include gridle_state(large) {
                width: $close-bt-size-large;
                height: $close-bt-size-large;
                transition: transform 0.4s ease-out 0.3s, opacity .3s ease .4s;
                right: $global-viewport-padding;
                top: $global-viewport-padding;
            }

            .cross {
                @include cross($white);
            }
        }

        .mobile-overlay {
            position: absolute;
            display: block;
            width: 100%;
            height: 0;
            top: 0;
            left: 0;
            opacity: 0;
            background-color: $black;
            transition: height .6s ease 0.2s, opacity .8s ease 0.2s;

            @include gridle_state(large) {
                display: none;
            }
        }

        .previous-hotspot,
        .next-hotspot {
            display: block;
            position: absolute;
            z-index: 6;
            top: 50%;
            left: 50%;
            padding: 1rem;
            cursor: pointer;
            transform: translateX(-50%) translateY(-50%);

            @include gridle_state(large) {
                display: none;
            }

            &.previous-hotspot {
                margin-left: -44%;

                @include gridle_state(small) { margin-left: -38%; }
                @include gridle_state(medium) { margin-left: -30%; }
            }

            &.next-hotspot {
                margin-left: 44%;

                @include gridle_state(small) { margin-left: 38%; }
                @include gridle_state(medium) { margin-left: 30%; }
            }
        }

        .hotspot-xray-area {
            position: absolute;
            display: block;
            width: 100%;
            height: 100%;
            transition: translateX(-50%) translateY(-50%);
            top: 0;
            left: 0;
            background-repeat: no-repeat;
            background-size: cover;
            background-position: center;

            @include gridle_state(large) {
                cursor: none;
            }

            svg {
                position: absolute;
                display: block;
                top: 0; left: 0;
                z-index: 1;
                transform: scale(0);
                width: 100%; height: 100%;
                opacity: 0;
                transition: transform .6s ease 1.2s, opacity .2s ease 1.2s;

                @include gridle_state(large) {
                    transition: transform .6s ease 1.3s, opacity .2s ease 1.3s;
                }

                circle {
                    stroke: $white;
                    stroke-width: 10;
                }

                image {
                    cursor: pointer;

                    @include gridle_state(large) {
                        cursor: none;
                    }
                }
            }
        }

        .row {
            width: 100vw;
            height: 100%;
        }

        // opening
        &.active {
            width: 100%;
            opacity: 1;

            @include gridle_state(large) {
                transition: opacity .2s ease .2s;
                animation: hotspot-panel-in .2s ease forwards;
            }

            svg {
                transform: scale(1);
                opacity: 1;
            }

            .back-to-360 {
                opacity: 1;
            }

            .hotspot-content {
                transform: translateX(0);

                .md-column > * {
                    left: 0;
                }
            }

            .hotspot-imagery {
                transform: translateX(0);
            }

            @include gridle_state(large_max) {
                .mobile-overlay {
                    height: 100%;
                    opacity: 0.55;
                }
            }
        }

        $hotspots-nav-dot-size: 6px;
        $hotspots-nav-dot-size-outter: 16px;

        .hotspot-mobile-line {
            display: block;
            position: absolute;
            left: 50%;
            bottom: 4rem;
            transform: translateX(-50%);
            width: 100%;
            height: 1px;
            background-color: rgba($white, .4);

            @include gridle_state(large) {
                display: none;
            }

            .hotspot-mobile-line-dots {
                @include unstyleList();

                li {
                    position: absolute;
                    display: block;
                    width: $hotspots-nav-dot-size;
                    height: $hotspots-nav-dot-size;
                    border-radius: 50%;
                    top: 50%;
                    transform: translateX(-50%) translateY(-50%);

                    &:nth-child(1) {
                        left: 15%;
                        opacity: 0.5;
                    }

                    &:nth-child(2) {
                        left: 50%;
                        background-color: $white;

                        &::before {
                            content: '';
                            display: block;
                            z-index: 2;
                            border-radius: 50%;
                            position: absolute;
                            top: 50%;
                            left: 50%;
                            transform: translateX(-50%) translateY(-50%);
                            border: 1px solid $white;
                            opacity: 0.5;
                            width: $hotspots-nav-dot-size-outter;
                            height: $hotspots-nav-dot-size-outter;
                        }

                        .hotspot-number {
                            margin-top: -.5rem;
                        }
                    }

                    &:nth-child(3) {
                        right: 15%;
                        opacity: 0.5;
                    }

                    .hotspot-number,
                    .hotspot-title {
                        @include headingsStyle();
                        color: $white;
                        letter-spacing: 2px;
                        font-size: 0.85rem;
                        position: absolute;
                        display: block;
                        padding: 2rem;
                        white-space: nowrap;
                        transform: translateX(-50%) translateY(-50%);
                        left: 50%;
                    }

                    .hotspot-number {
                        top: -1.5rem;
                    }

                    .hotspot-title {
                        top: 1.75rem;
                    }
                }

            }
        }

        // left panel
        .hotspot-imagery {
            background-color: $gray-blue;
            height: calc(100% - #{$mobile-button-height});
            position: absolute;
            background-position: center;
            background-size: cover;
            background-repeat: no-repeat;
            top: 0; left: 0;
            transform: translateX(100%);

            @include gridle_grid(12);

            @include gridle_state(large) {
                height: 100%;
                transition: transform .6s ease-out .1s;
                @include gridle_grid(6);
            }

            @include gridle_state(xlarge) {
                @include gridle_grid(7);
            }

            @include gridle_state(xxlarge) {
                @include gridle_grid(8);
            }
        }

        // right panel
        .hotspot-content {
            transform: translateX(100%);
            background-color: $white;
            height: 100%;
            padding: $global-viewport-padding 3rem 0rem;
            position: absolute;
            top: 0; right: 0;

            @include gridle_state(large) {
                transition: transform .5s ease-out .1s;
            }

            .md-column > * {
                left: 150%;
                position: relative;

                @include gridle_state(large) {
                    transition: left .6s ease;

                    @for $i from 1 through 20 {
                        &:nth-child(#{$i}) {
                            transition-delay: .15s * $i;
                        }
                    }
                }
            }

            @include gridle_state(large_max) {
                transition: top .6s ease;
                padding: 5rem 1rem 0rem 1.5rem;
                display: block;
                position: fixed;
                top: calc(100% - #{$mobile-button-height});
                left: 0;
                width: 100%;
                height: 100%;
                z-index: 999;

                &.expanded {
                    top: 0;
                }
            }

            .expand-collapse-bt {
                transition: background .6s ease;
                position: absolute;
                display: block;
                width: 100%;
                left: 0;
                top: 0;
                margin: 0;

                .icon {
                    transition: transform .6s ease;

                    i {
                        animation: up-n-down 1.5s linear infinite;
                    }
                }

                @include gridle_state(large) {
                    display: none;
                }
            }

            &.expanded {
                .expand-collapse-bt {
                    background-color: darken($white, 4%);

                    .icon {
                        transform: rotate(180deg) translateY(50%);

                        i {
                            animation: none;
                        }
                    }
                }
            }

            .label {
                @include headingsStyle();
                font-size: 0.8rem;
                letter-spacing: 1px;
                position: relative;
                display: inline-block;
                border: 1px solid lighten($gray-blue, 25%);
                color: $gray-blue;
                border-radius: 15px;
                padding: 0.4rem 1.2rem 0.3rem;
                margin-top: $close-bt-size/2 - 13px;
                margin-left: -.25rem;
            }

            .markdown {
                text-align: left;

                img {
                    width: 100%;
                }
            }

            .title {
                margin: 1rem 0 .5rem;
            }
        }

        .scrolly-me-container {
            height: calc(100% - #{$global-viewport-padding-mobile} - #{$close-bt-size} - 3rem);

            @include gridle_state(large) {
                height: calc(100% - #{$global-viewport-padding} - #{$close-bt-size} - 2.5rem);
            }

            .scrolly-me {
                padding-right: 2.5rem;
            }

            .ps-scrollbar-x-rail,
            .ps-scrollbar-y-rail {
                opacity: 0.6 !important;
            }
        }
    }

</style>



// WEBPACK FOOTER //
// HotspotPanel.vue?05c2e47a